import {useGetPatient} from '@/modules/patients/api/use-get-patient'
import {useGetPatientTherapies} from '@/modules/patients/api/use-get-patient-therapies'
import {changeDoseUnitLabel} from '@/modules/protocols/functions'
import {useGetProtocol} from '@/modules/protocols/api/use-get-protocol'
import {useGetSettings} from '@/modules/settings/api'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {DateValue, parseDate} from '@ark-ui/react/date-picker'
import {Alert, Badge, Box, Button, ButtonGroup, HStack, Table, Text, useDialogContext, VStack} from '@chakra-ui/react'
import {PatientProtocol, PatientTherapy} from '@cypro/engine/queries'
import {addDaysToIsoDate, daysBetweenIsoDates, isoDateFromTimestamp, timestampFromIsoDate} from '@cypro/engine/utils'
import {dialog, DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {Tooltip} from '@cypro/ui/components/ui/tooltip'
import {useAppForm} from '@cypro/ui/integrations/form'
import {toaster} from '@cypro/ui/toaster'
import {getShortDateFormatter, isDefined} from '@cypro/ui/utils'
import {useStore} from '@tanstack/react-form'
import {formatIsoDate} from '@/shared/utils/dates'
import {useTranslate} from '@tolgee/react'
import {Fragment, useEffect, useMemo, useState} from 'react'
import {LuTrash2} from 'react-icons/lu'
import {uuidv7} from 'uuidv7'
import {ProtocolChangeSummary, SaveProtocolChangesDialog} from './save-protocol-changes-dialog'

interface CycleFormRow {
  id: string
  sourceCycle: number
  cycle: number
  startDate: DateValue
  isNew: boolean
}

type CycleWarning =
  | {type: 'beforeProtocolStart'; protocolStart: string}
  | {type: 'overlap'; nextCycle: number; nextStart: string; lastPrescriptionDate: string; shiftDays: number}
  | {type: 'moved'; fromDate: string; toDate: string; shiftedCount: number}
  | {type: 'new'}

function isBeforePharmacyValidation(seq: PatientTherapy): boolean {
  return (
    seq.status === 'pending_approval' ||
    seq.status === 'pending_doctor_validation' ||
    (seq.status === 'validating' && !seq.pharmacistValidatedAt)
  )
}

function iso(row: CycleFormRow): string {
  return row.startDate.toString()
}

export interface EditProtocolDialogProps {
  patientProtocol: PatientProtocol
}

export function EditProtocolDialog({patientProtocol}: EditProtocolDialogProps) {
  const {t} = useTranslate('common')
  const context = useDialogContext()
  const zero = useZero()
  const [patientTherapies] = useGetPatientTherapies(patientProtocol.patientId)
  const [patient] = useGetPatient(patientProtocol.patientId)
  const [protocol] = useGetProtocol(patientProtocol.protocolId)
  const {settings} = useGetSettings()

  const daysRepetition = protocol?.daysRepetition ?? 0
  // Whether `day` is 0-indexed (first day of a cycle is day 0) or 1-indexed (first day is day
  // 1) - same convention as computeTherapyDateFromCycleDay/add-parameter-dialog-new.tsx.
  const dayZeroProtocol = Boolean(settings?.dayZeroProtocol)

  const protocolSequences = useMemo(
    () => patientTherapies.filter(therapy => therapy.patientProtocolId === patientProtocol.id),
    [patientTherapies, patientProtocol.id]
  )

  const templateActiveSequences = useMemo(
    () => protocol?.therapySequence?.filter(seq => !seq.isNote) ?? [],
    [protocol?.therapySequence]
  )

  const initialCycles = useMemo<{cycle: number; startDate: string}[]>(() => {
    const totalCycles = patientProtocol.cycles ?? protocol?.totalCycles ?? 1
    const rows: {cycle: number; startDate: string}[] = []
    for (let cycle = 1; cycle <= totalCycles; cycle++) {
      const startDate =
        patientProtocol.cycleStartDates?.[cycle] ??
        addDaysToIsoDate(isoDateFromTimestamp(patientProtocol.startDate), (cycle - 1) * daysRepetition)
      rows.push({cycle, startDate})
    }
    return rows
  }, [
    patientProtocol.cycles,
    patientProtocol.cycleStartDates,
    patientProtocol.startDate,
    protocol?.totalCycles,
    daysRepetition,
  ])

  const initialCyclesByNumber = useMemo(() => new Map(initialCycles.map(c => [c.cycle, c.startDate])), [initialCycles])
  // a new cycle can reuse a deleted cycle's number as its sourceCycle, so it never has a saved date
  const getOriginalIso = (row: CycleFormRow) => (row.isNew ? undefined : initialCyclesByNumber.get(row.sourceCycle))
  const protocolStartIso = isoDateFromTimestamp(patientProtocol.startDate)

  // Computed once on mount: `defaultValues` must stay referentially stable across renders,
  // otherwise useAppForm re-initializes on every render (new uuids every time) and loops.
  const [initialCyclesForForm] = useState(() =>
    initialCycles.map(c => ({
      id: uuidv7(),
      sourceCycle: c.cycle,
      cycle: c.cycle,
      startDate: parseDate(c.startDate),
      isNew: false,
    }))
  )

  const form = useAppForm({
    defaultValues: {
      cycles: initialCyclesForForm,
    },
  })

  const cycles = useStore(form.store, state => state.values.cycles)

  useEffect(() => {
    if (cycles.length === 0 && initialCycles.length > 0) {
      form.setFieldValue(
        'cycles',
        initialCycles.map(c => ({
          id: uuidv7(),
          sourceCycle: c.cycle,
          cycle: c.cycle,
          startDate: parseDate(c.startDate),
          isNew: false,
        })),
        {dontRunListeners: true, dontUpdateMeta: true}
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCycles])

  function getCycleSequences(cycle: number) {
    return protocolSequences.filter(seq => seq.cycle === cycle && !seq.isNote)
  }

  function isCycleInProgress(row: CycleFormRow): boolean {
    if (row.isNew) return false
    // A cycle is in progress once any of its prescriptions is pharmacy validated (or further along).
    return getCycleSequences(row.sourceCycle).some(
      seq => seq.status !== 'cancelled' && !isBeforePharmacyValidation(seq)
    )
  }

  function getMaxDayOffset(row: CycleFormRow): number {
    const sequences = row.isNew ? templateActiveSequences : getCycleSequences(row.sourceCycle)
    if (!sequences.length) return 0
    const maxDay = Math.max(...sequences.map(seq => seq.day ?? 0))
    return dayZeroProtocol ? maxDay : maxDay - 1
  }

  function isBeforeProtocolStart(row: CycleFormRow): boolean {
    // Only edited or new cycles: an untouched legacy cycle must not block saving other changes.
    const isEdited = row.isNew || getOriginalIso(row) !== iso(row)
    return isEdited && iso(row) < protocolStartIso
  }

  function getWarning(row: CycleFormRow, index: number, rows: CycleFormRow[]): CycleWarning | undefined {
    const nextRow = rows[index + 1]
    const startIso = iso(row)
    if (isBeforeProtocolStart(row)) {
      return {type: 'beforeProtocolStart', protocolStart: protocolStartIso}
    }
    if (nextRow) {
      const lastPrescriptionDate = addDaysToIsoDate(startIso, getMaxDayOffset(row))
      if (lastPrescriptionDate >= iso(nextRow)) {
        return {
          type: 'overlap',
          nextCycle: nextRow.cycle,
          nextStart: iso(nextRow),
          lastPrescriptionDate,
          shiftDays: daysBetweenIsoDates(iso(nextRow), lastPrescriptionDate) + 1,
        }
      }
    }
    const original = getOriginalIso(row)
    if (original && startIso !== original) {
      return {
        type: 'moved',
        fromDate: original,
        toDate: startIso,
        shiftedCount: getCycleSequences(row.sourceCycle).length,
      }
    }
    if (row.isNew) {
      return {type: 'new'}
    }
    return undefined
  }

  // 'original' shifts each following row relative to its own saved date, so re-clicking after
  // the shift has already been applied is a no-op instead of compounding on every click.
  function handleShiftFollowing(afterCycle: number, deltaDays: number, basis: 'original' | 'current') {
    form.setFieldValue('cycles', prev =>
      prev.map(row => {
        if (row.cycle <= afterCycle || isCycleInProgress(row)) return row
        const baseIso = basis === 'original' ? (getOriginalIso(row) ?? iso(row)) : iso(row)
        return {...row, startDate: parseDate(addDaysToIsoDate(baseIso, deltaDays))}
      })
    )
  }

  function isFollowingAlreadyShifted(afterCycle: number, deltaDays: number): boolean {
    return cycles
      .filter(row => row.cycle > afterCycle && !isCycleInProgress(row))
      .every(row => {
        const baseIso = getOriginalIso(row) ?? iso(row)
        return iso(row) === addDaysToIsoDate(baseIso, deltaDays)
      })
  }

  function handleRemoveCycle(cycle: number) {
    form.setFieldValue('cycles', prev =>
      prev.filter(row => row.cycle !== cycle).map(row => (row.cycle > cycle ? {...row, cycle: row.cycle - 1} : row))
    )
  }

  function handleAddCycle() {
    const lastRow = cycles[cycles.length - 1]
    const nextCycle = (cycles.length ? Math.max(...cycles.map(row => row.cycle)) : 0) + 1
    const baseIso = lastRow ? iso(lastRow) : isoDateFromTimestamp(patientProtocol.startDate)
    const startIso = lastRow ? addDaysToIsoDate(baseIso, daysRepetition) : baseIso
    form.pushFieldValue('cycles', {
      id: uuidv7(),
      sourceCycle: nextCycle,
      cycle: nextCycle,
      startDate: parseDate(startIso),
      isNew: true,
    })
  }

  function buildChangeSummary(): ProtocolChangeSummary {
    const survivingSourceCycles = new Set(cycles.filter(row => !row.isNew).map(row => row.sourceCycle))

    const moved: ProtocolChangeSummary['moved'] = []
    for (const row of cycles) {
      if (row.isNew) continue
      const original = initialCyclesByNumber.get(row.sourceCycle)
      if (!original || iso(row) === original) continue
      const sequences = getCycleSequences(row.sourceCycle)
      moved.push({
        cycle: row.cycle,
        fromDate: original,
        toDate: iso(row),
        shiftedCount: sequences.length,
        validationsRemoved: sequences.filter(seq => seq.doctorValidatedAt != null).length,
      })
    }

    const deletedCycleNumbers = initialCycles.map(c => c.cycle).filter(c => !survivingSourceCycles.has(c))
    const renumberedRows = cycles.filter(row => !row.isNew && row.cycle !== row.sourceCycle)
    const deleted: ProtocolChangeSummary['deleted'] = deletedCycleNumbers.map(deletedCycle => {
      const renumbered = renumberedRows
        .filter(row => {
          const precedingDeletions = deletedCycleNumbers.filter(d => d < row.sourceCycle)
          return precedingDeletions.length > 0 && Math.max(...precedingDeletions) === deletedCycle
        })
        .map(row => ({from: row.sourceCycle, to: row.cycle}))
      return {
        originalCycle: deletedCycle,
        prescriptionsCancelled: getCycleSequences(deletedCycle).length,
        renumbered,
      }
    })

    const added: ProtocolChangeSummary['added'] = cycles
      .filter(row => row.isNew)
      .map(row => ({cycle: row.cycle, startDate: iso(row), prescriptionsCount: templateActiveSequences.length}))

    return {moved, deleted, added}
  }

  function handleSaveClick() {
    const changes = buildChangeSummary()
    const cyclesPayload = cycles.map(row => ({
      cycle: row.cycle,
      sourceCycle: row.isNew ? null : row.sourceCycle,
      startDate: timestampFromIsoDate(iso(row)),
    }))
    const deletedCyclesPayload = changes.deleted.map(change => change.originalCycle)

    void dialog.open('save-protocol-changes', {
      children: (
        <SaveProtocolChangesDialog
          changes={changes}
          onConfirm={async reason => {
            await zero.attemptMutation(
              zero.mutators.patients.updateProtocolCycles({
                patientProtocolId: patientProtocol.id,
                cycles: cyclesPayload,
                deletedCycles: deletedCyclesPayload,
                reason: reason || undefined,
              }),
              async mutation => {
                const data = await mutation.server
                if (!data) return
                toaster.success({title: t('success'), description: t('patient_updated_successfully')})
                onClose()
              }
            )
          }}
        />
      ),
      contentProps: {minWidth: '32vw'},
    })
  }

  const hasChanges =
    cycles.length !== initialCycles.length ||
    cycles.some(row => {
      const original = getOriginalIso(row)
      return original === undefined || original !== iso(row)
    })

  const hasOverlap = cycles.some((row, index) => getWarning(row, index, cycles)?.type === 'overlap')
  const hasCycleBeforeProtocolStart = cycles.some(isBeforeProtocolStart)

  const currentCycle = cycles.filter(row => isCycleInProgress(row)).length || 1
  const dateFormatter = getShortDateFormatter()

  function getSequenceLabel(seq: PatientTherapy) {
    const medicine = seq.medicine?.[0]
    const name = medicine?.prescribedName?.name || medicine?.activeSubstance?.name || t('not_available', 'N/A')
    const unit = medicine
      ? changeDoseUnitLabel(medicine.calculationMethod ?? 'ABSOLUTE', medicine.activeSubstance ?? undefined)
      : ''
    return `${t('day')} ${seq.day} - ${name}${medicine?.regularDose != null ? ` - ${medicine.regularDose}${unit}` : ''}`
  }

  function renderPrescriptionsCell(row: CycleFormRow) {
    if (isCycleInProgress(row)) {
      const sequences = getCycleSequences(row.sourceCycle)
      const administered = sequences.filter(seq => seq.status === 'administered').length
      return (
        <Text fontSize="sm">
          {sequences.length} · {administered} {t('administered', 'administered')}
        </Text>
      )
    }

    if (row.isNew) {
      return (
        <Text fontSize="sm">
          {templateActiveSequences.length} {t('planned', 'planned')}
        </Text>
      )
    }

    const sequences = getCycleSequences(row.sourceCycle)
    return (
      <Tooltip
        content={
          <VStack align="start" gap="0">
            {sequences.map(seq => (
              <Text key={seq.id} fontSize="xs">
                {getSequenceLabel(seq)}
              </Text>
            ))}
          </VStack>
        }
      >
        <Text as="span" fontSize="sm" color="cyan.fg" textDecoration="underline" cursor="pointer">
          {sequences.length} {t('planned', 'planned')}
        </Text>
      </Tooltip>
    )
  }

  function renderWarning(row: CycleFormRow, warning: CycleWarning) {
    if (warning.type === 'beforeProtocolStart') {
      return (
        <Alert.Root status="error" borderRadius="md" alignItems="center">
          <Alert.Indicator />
          <Alert.Content flex="1">
            <Alert.Title>{t('cycle_before_protocol_start', 'Cycle starts before the protocol')}</Alert.Title>
            <Alert.Description>
              {t('protocol.start_date')}: {formatIsoDate(warning.protocolStart)}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      )
    }

    if (warning.type === 'overlap') {
      return (
        <Alert.Root status="error" borderRadius="md" alignItems="center">
          <Alert.Indicator />
          <Alert.Content flex="1">
            <Alert.Title>
              {t('overlap_with_cycle', 'Overlap with cycle {{cycle}}', {cycle: warning.nextCycle})}
            </Alert.Title>
            <Alert.Description>
              {t('cycle_x_start', 'Cycle {{cycle}} start', {cycle: warning.nextCycle})}:{' '}
              {formatIsoDate(warning.nextStart)}
              {'   '}
              {t('cycle_x_last_prescription', 'Cycle {{cycle}} last prescription', {cycle: row.cycle})}:{' '}
              {formatIsoDate(warning.lastPrescriptionDate)}
              {'   '}
              {t('shift', 'Shift')}: +{warning.shiftDays} {t('days', 'days')}
            </Alert.Description>
          </Alert.Content>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              handleShiftFollowing(row.cycle, warning.shiftDays, 'current')
            }}
          >
            {t('shift_following_cycles', 'Shift the following cycles')}
          </Button>
        </Alert.Root>
      )
    }

    if (warning.type === 'moved') {
      const deltaDays = daysBetweenIsoDates(warning.fromDate, warning.toDate)
      const alreadyShifted = isFollowingAlreadyShifted(row.cycle, deltaDays)
      return (
        <Alert.Root status="warning" borderRadius="md" alignItems="center">
          <Alert.Indicator />
          <Alert.Content flex="1">
            <Alert.Title>{t('cycle_moved', 'Cycle moved')}</Alert.Title>
            <Alert.Description>
              {t('protocol.start_date')}: {formatIsoDate(warning.fromDate)} → {formatIsoDate(warning.toDate)}
              {'   '}
              {t('prescriptions_shifted', 'Prescriptions shifted')}: {warning.shiftedCount}
            </Alert.Description>
          </Alert.Content>
          {!alreadyShifted && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                handleShiftFollowing(row.cycle, deltaDays, 'original')
              }}
            >
              {t('shift_following_cycles', 'Shift the following cycles')}
            </Button>
          )}
        </Alert.Root>
      )
    }

    return (
      <Alert.Root status="info" borderRadius="md" alignItems="center">
        <Alert.Indicator />
        <Alert.Content flex="1">
          <Alert.Title>{t('new_cycle', 'New cycle')}</Alert.Title>
          <Alert.Description>
            {t(
              'new_cycle_description',
              'Prescriptions are copied from the protocol template and need doctor validation.'
            )}
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>
    )
  }

  function onClose() {
    context.setOpen(false)
  }

  const patientName = patient
    ? [patient.title ? t(patient.title) : '', patient.givenName, patient.familyName].filter(Boolean).join(' ')
    : ''
  const diagnosisName = patientProtocol.protocol?.diagnosis?.name

  if (patientProtocol.endDate) {
    return (
      <>
        <DialogHeader>
          <Text fontSize="lg" fontWeight="semibold">
            {t('protocol_cannot_be_edited', 'Protocol cannot be edited')}
          </Text>
        </DialogHeader>
        <DialogBody minHeight="none">
          <Text fontSize="sm" color="fg.muted">
            {t(
              'stopped_protocol_cannot_be_edited_description',
              'This protocol is stopped. Reactivate it before editing its cycles.'
            )}
          </Text>
        </DialogBody>
        <DialogFooter>
          <ButtonGroup size="md" width="100%" justifyContent="flex-end">
            <Button variant="outline" colorPalette="gray" onClick={onClose}>
              {t('close')}
            </Button>
          </ButtonGroup>
        </DialogFooter>
      </>
    )
  }

  return (
    <>
      <DialogHeader>
        <Text fontSize="lg" fontWeight="semibold">
          {t('edit_protocol', 'Edit protocol')}
        </Text>
        <Text fontSize="sm" color="fg.muted" fontWeight="normal">
          {[
            patientProtocol.protocol?.name,
            isDefined(patient?.birthDate) ? `${patientName} (${dateFormatter.format(patient.birthDate)})` : '',
            diagnosisName,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      </DialogHeader>

      <DialogBody minHeight="none">
        <VStack spaceY="4" width="100%" align="stretch">
          <Box
            display="flex"
            width="100%"
            bg="bg.subtle"
            borderRadius="md"
            borderWidth="1px"
            borderColor="border"
            p="4"
            gap="8"
          >
            <Box display="flex" flexDirection="column" gap="1">
              <Text fontSize="sm" color="fg.muted">
                {t('doctor_responsible', 'Doctor (responsible)')}
              </Text>
              <Text fontSize="sm" fontWeight="semibold">
                {patientProtocol.created?.displayUsername ?? '-'}
              </Text>
            </Box>
            <Box display="flex" flexDirection="column" gap="1">
              <Text fontSize="sm" color="fg.muted">
                {t('started', 'Started')}
              </Text>
              <Text fontSize="sm" fontWeight="semibold">
                {dateFormatter.format(patientProtocol.startDate)}
              </Text>
            </Box>
            <Box display="flex" flexDirection="column" gap="1">
              <Text fontSize="sm" color="fg.muted">
                {t('progress', 'Progress')}
              </Text>
              <Text fontSize="sm" fontWeight="semibold">
                {t('cycle')} {currentCycle}/{cycles.length}
              </Text>
            </Box>
          </Box>

          <Box display="flex" width="100%" justifyContent="space-between" alignItems="center">
            <Text fontSize="md" fontWeight="semibold">
              {t('cycles', 'Cycles')}
            </Text>
            <Button size="sm" variant="outline" onClick={handleAddCycle}>
              + {t('add_cycle', 'Add cycle')}
            </Button>
          </Box>

          <Box width="100%" maxHeight="24rem" overflowY="auto" borderRadius="md">
            <Table.Root size="sm" variant="outline">
              <Table.Header position="sticky" top="0" zIndex="1" bg="bg">
                <Table.Row>
                  <Table.ColumnHeader>{t('cycle')}</Table.ColumnHeader>
                  <Table.ColumnHeader>{t('protocol.start_date')}</Table.ColumnHeader>
                  <Table.ColumnHeader>{t('prescriptions', 'Prescriptions')}</Table.ColumnHeader>
                  <Table.ColumnHeader>{t('status')}</Table.ColumnHeader>
                  <Table.ColumnHeader></Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {cycles.map((row, index) => {
                  const warning = getWarning(row, index, cycles)
                  const inProgress = isCycleInProgress(row)
                  return (
                    <Fragment key={row.id}>
                      <Table.Row>
                        <Table.Cell>
                          <HStack gap="2">
                            <Text fontWeight="semibold">
                              {t('cycle')} {row.cycle}
                            </Text>
                            {row.isNew && (
                              <Badge size="sm" colorPalette="blue" variant="subtle">
                                {t('new', 'New')}
                              </Badge>
                            )}
                          </HStack>
                        </Table.Cell>
                        <Table.Cell>
                          {inProgress ? (
                            <Text color="fg.subtle">{formatIsoDate(iso(row))}</Text>
                          ) : (
                            <Box maxWidth="10rem">
                              <form.AppField name={`cycles[${index}].startDate`} mode="value">
                                {field => (
                                  <field.DatePicker
                                    label={t('protocol.start_date')}
                                    hideLabel
                                    size="sm"
                                    minDate={parseDate(protocolStartIso)}
                                  />
                                )}
                              </form.AppField>
                            </Box>
                          )}
                        </Table.Cell>
                        <Table.Cell>{renderPrescriptionsCell(row)}</Table.Cell>
                        <Table.Cell>
                          <Badge colorPalette={inProgress ? 'cyan' : 'gray'} variant="subtle">
                            {inProgress ? t('in_progress', 'In progress') : t('planned', 'Planned')}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          {!inProgress && (
                            <Button
                              size="sm"
                              variant="outline"
                              colorPalette="red"
                              onClick={() => {
                                handleRemoveCycle(row.cycle)
                              }}
                            >
                              <LuTrash2 />
                              {row.isNew ? t('remove', 'Remove') : t('delete')}
                            </Button>
                          )}
                        </Table.Cell>
                      </Table.Row>
                      {warning && (
                        <Table.Row>
                          <Table.Cell colSpan={5} paddingY="2">
                            {renderWarning(row, warning)}
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </Fragment>
                  )
                })}
              </Table.Body>
            </Table.Root>
          </Box>
        </VStack>
      </DialogBody>

      <DialogFooter>
        <ButtonGroup size="md" colorPalette="cyan" width="100%" justifyContent="flex-end" alignItems="center">
          {hasCycleBeforeProtocolStart ? (
            <Text fontSize="sm" color="red.fg" marginRight="auto">
              {t('cycle_before_protocol_start_to_save', 'Cycles cannot start before the protocol start date.')}
            </Text>
          ) : (
            hasOverlap && (
              <Text fontSize="sm" color="red.fg" marginRight="auto">
                {t('resolve_overlap_to_save', 'Resolve the overlap to save.')}
              </Text>
            )
          )}
          <Button variant="outline" colorPalette="gray" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button disabled={!hasChanges || hasOverlap || hasCycleBeforeProtocolStart} onClick={handleSaveClick}>
            {t('save_changes', 'Save changes')}
          </Button>
        </ButtonGroup>
      </DialogFooter>
    </>
  )
}
